import { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPost, ApiError } from '../lib/apiClient';

/**
 * F4 — service-card changes awaiting a second admin (docs/administration.md §24.8.5).
 *
 * Not content moderation, despite living under the same sub-project: service cards
 * are admin-authored, so this is two-person review at the same privilege level.
 *
 * Sits on the Service Cards page rather than in the moderation queue, for two
 * reasons: it is a different role (ADMIN, where the queue admits SUPPORT), and this
 * is where an admin already is when a change needs approving.
 *
 * **The single-admin case is the default here**, not an edge case — production has
 * nine users. So this panel states plainly when a self-approval is what will happen,
 * rather than letting an admin discover it from an audit log later.
 */
interface PendingChange {
  id: string;
  cardId: string;
  changes: Record<string, unknown>;
  proposedBy: string;
  proposedAt?: string;
  status: 'pending' | 'approved' | 'rejected';
}

/**
 * Render a proposed change set compactly.
 *
 * Shown as `field: value` pairs rather than raw JSON — an approver has to be able to
 * read what they are agreeing to, and this is the screen that decides whether a
 * price change reaches customers.
 */
export function describeChanges(changes: Record<string, unknown> | undefined): string {
  if (!changes || Object.keys(changes).length === 0) return '—';
  return Object.entries(changes)
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(' · ');
}

/** Whether approving this change would be a self-approval by the current admin. */
export function isSelfApproval(change: PendingChange, currentUid: string | undefined): boolean {
  return Boolean(currentUid) && change.proposedBy === currentUid;
}

export function ServiceCardApprovalsPanel({ currentUid }: { currentUid?: string }) {
  const [items, setItems] = useState<PendingChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet<{ items: PendingChange[] }>('/admin/service-cards/approvals/pending');
      setItems(data.items ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load pending changes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (id: string, path: string, body?: unknown) => {
    setBusyId(id);
    setError(null);
    try {
      await apiPost(`/admin/service-cards/approvals/${encodeURIComponent(id)}/${path}`, body);
      setRejectingId(null);
      setReason('');
      void load();
    } catch (err) {
      // A 403 here is the four-eyes rule refusing a self-approval, which is a
      // meaningful answer rather than a fault — surface the server's wording.
      setError(err instanceof ApiError ? err.message : `Failed to ${path} the change`);
    } finally {
      setBusyId(null);
    }
  };

  // Nothing pending is the normal state; don't occupy the page with an empty table.
  if (!loading && items.length === 0 && !error) return null;

  return (
    <div className="card" style={{ marginBottom: '1.5rem' }}>
      <div className="section-header">
        <div>
          <h2 style={{ marginBottom: 0 }}>Changes awaiting approval</h2>
          <p className="cell-sub">
            A service-card change is not live until an admin approves it. If you are the only
            admin, approving your own change is allowed and is recorded as a self-approval.
          </p>
        </div>
        <button type="button" className="btn btn-xs" onClick={load}>
          Refresh
        </button>
      </div>

      {error && (
        <div role="alert" className="error-banner">
          {error}
        </div>
      )}

      {loading && <p className="cell-sub">Loading…</p>}

      <div className="table-wrap">
        <table className="data-table dt-table">
          <thead>
            <tr>
              <th>Card</th>
              <th>Proposed change</th>
              <th>Proposed by</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id}>
                <td>{c.cardId}</td>
                <td>{describeChanges(c.changes)}</td>
                <td>
                  {c.proposedBy}
                  {isSelfApproval(c, currentUid) && (
                    <div className="cell-sub">You proposed this — approving is a self-approval</div>
                  )}
                </td>
                <td>
                  {rejectingId === c.id ? (
                    <div className="row-actions">
                      <input
                        autoFocus
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Reason (shown to the proposer)"
                        aria-label={`Rejection reason for ${c.cardId}`}
                      />
                      <button
                        type="button"
                        className="btn btn-xs btn-primary"
                        disabled={!reason.trim() || busyId === c.id}
                        onClick={() => void act(c.id, 'reject', { reason })}
                      >
                        Confirm reject
                      </button>
                      <button
                        type="button"
                        className="btn btn-xs"
                        onClick={() => {
                          setRejectingId(null);
                          setReason('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="row-actions">
                      <button
                        type="button"
                        className="btn btn-xs btn-primary"
                        disabled={busyId === c.id}
                        onClick={() => void act(c.id, 'approve')}
                      >
                        Approve &amp; apply
                      </button>
                      <button
                        type="button"
                        className="btn btn-xs"
                        disabled={busyId === c.id}
                        onClick={() => {
                          setRejectingId(c.id);
                          setReason('');
                        }}
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ServiceCardApprovalsPanel;
